import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Label,
  LabeledInput,
  LabeledSelect,
  ProgressRadial,
  Flex,
  Text,
} from "@itwin/itwinui-react";
import { useCreateSubscription } from "../../hooks/useCreateSubscription/useCreateSubscription";
import { ISubscriptionCreateRequest } from "../../interfaces/ISubscriptionCreateRequest";
import { ISubscription } from "../../interfaces/ISubscriptions";
import { IDiscovery } from "../../interfaces/IDiscovery";
import { EventSelector } from "../eventSelector/eventSelector";
import { DataCenterSelect } from "../dataCenterSelect/dataCenterSelect";
import { useGetDataCenters } from "../../hooks/useGetDataCenters/useGetDataCenters";

interface SubscriptionCreationState extends ISubscriptionCreateRequest {
  useAllEvents: boolean;
}

export const SubscriptionCreation = (props: {
  consumerId: string;
  discovery: IDiscovery;
  setNewSubscription: (subscription: ISubscription) => void;
}) => {
  const { consumerId, discovery, setNewSubscription } = props;
  const [subscriptionCreation, setSubscriptionCreation] =
    useState<SubscriptionCreationState>({
      producerId: "",
      status: "Active",
      accountIdFilter: undefined,
      iTwinIdFilter: undefined,
      resourceDataCenterIdFilter: undefined,
      iTwinDataCenterIdFilter: undefined,
      events: [],
      useAllEvents: true,
    });
  const [subscription, loading, error, createSubscription] =
    useCreateSubscription();

  const [dataCentersResponse, dataCentersLoading, dataCentersError, getDataCenters] = useGetDataCenters();

  // Fetch data centers on mount
  useEffect(() => {
    getDataCenters();
  }, [getDataCenters]);

  const producerNameOptions = useMemo(() => {
    return discovery.producers.reduce((acc, cur) => {
      acc.push({ label: cur.name, value: cur.id });
      return acc;
    }, [] as Array<{ label: string; value: string }>);
  }, [discovery.producers]);

  const selectedProducer = useMemo(
    () => discovery.producers.find((p) => p.id === subscriptionCreation.producerId),
    [discovery.producers, subscriptionCreation.producerId]
  );

  const handleEventsChange = useCallback((events: string[], useAllEvents: boolean) => {
    setSubscriptionCreation((prev) => ({
      ...prev,
      events,
      useAllEvents,
    }));
  }, []);

  // Reset to "all events" when producer changes
  useEffect(() => {
    setSubscriptionCreation((prev) => ({
      ...prev,
      events: [],
      useAllEvents: true,
    }));
  }, [subscriptionCreation.producerId]);

  useEffect(() => {
    if (
      error === undefined &&
      subscription !== undefined &&
      subscription?.subscription !== undefined
    ) {
      console.log("Setting new sub");
      setNewSubscription(subscription as ISubscription);
    }
  }, [error, subscription]);

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Disabled", label: "Disabled" },
  ];

  // Validation: either useAllEvents is true, or there are selected events
  const canCreate = subscriptionCreation.producerId && 
    (subscriptionCreation.useAllEvents || subscriptionCreation.events.length > 0);

  const handleCreate = useCallback(() => {
    // When creating, if useAllEvents is true, send empty array to API
    const request: ISubscriptionCreateRequest = {
      producerId: subscriptionCreation.producerId,
      status: subscriptionCreation.status,
      accountIdFilter: subscriptionCreation.accountIdFilter,
      iTwinIdFilter: subscriptionCreation.iTwinIdFilter,
      resourceDataCenterIdFilter: subscriptionCreation.resourceDataCenterIdFilter,
      iTwinDataCenterIdFilter: subscriptionCreation.iTwinDataCenterIdFilter,
      events: subscriptionCreation.useAllEvents ? [] : subscriptionCreation.events,
    };
    createSubscription(consumerId, request);
  }, [consumerId, subscriptionCreation, createSubscription]);

  return (
    <>
      <LabeledSelect
        label="Producer"
        options={producerNameOptions ?? []}
        value={subscriptionCreation.producerId}
        onChange={(value) =>
          setSubscriptionCreation({
            ...subscriptionCreation,
            producerId: value,
          })
        }
        style={{ marginBottom: "12px", width: "50%" }}
      />
      <Label>Events</Label>
      {subscriptionCreation.producerId !== "" ? (
        <EventSelector
          eventDefinitions={selectedProducer?.eventDefinitions ?? []}
          selectedEvents={subscriptionCreation.events}
          useAllEvents={subscriptionCreation.useAllEvents}
          onEventsChange={handleEventsChange}
        />
      ) : (
        <Text isMuted style={{ marginBottom: "12px" }}>
          Select a producer to view available events
        </Text>
      )}

      <LabeledSelect
        label="Status"
        options={statusOptions}
        value={subscriptionCreation.status}
        onChange={(value) =>
          setSubscriptionCreation({
            ...subscriptionCreation,
            status: value,
          })
        }
        style={{ marginBottom: "12px", marginTop: "12px", width: "50%" }}
      />
      <Flex flexDirection="row" style={{ width: "100%" }} flexWrap="wrap">
        <Flex.Item flex="1 1 33%">
          <LabeledInput
            label="Account Id Filter"
            value={subscriptionCreation.accountIdFilter}
            onChange={(event) =>
              setSubscriptionCreation({
                ...subscriptionCreation,
                accountIdFilter: event.target.value,
              })
            }
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 33%">
          <LabeledInput
            label="iTwin Id Filter"
            value={subscriptionCreation.iTwinIdFilter}
            onChange={(event) =>
              setSubscriptionCreation({
                ...subscriptionCreation,
                iTwinIdFilter: event.target.value,
              })
            }
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 33%">
          <DataCenterSelect
            label="Resource Data Center Id"
            value={subscriptionCreation.resourceDataCenterIdFilter}
            onChange={(value) =>
              setSubscriptionCreation({
                ...subscriptionCreation,
                resourceDataCenterIdFilter: value,
              })
            }
            dataCenters={dataCentersResponse?.dataCenters ?? []}
            isLoading={dataCentersLoading}
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 33%">
          <DataCenterSelect
            label="iTwin Data Center Id"
            value={subscriptionCreation.iTwinDataCenterIdFilter}
            onChange={(value) =>
              setSubscriptionCreation({
                ...subscriptionCreation,
                iTwinDataCenterIdFilter: value,
              })
            }
            dataCenters={dataCentersResponse?.dataCenters ?? []}
            isLoading={dataCentersLoading}
            style={{ marginBottom: "12px" }}
          />
        </Flex.Item>
      </Flex>
      {error !== undefined || subscription?.error?.message !== undefined ? (
        <Text style={{ color: "red" }}>
          {subscription?.error?.message ?? error?.message ?? "Unknown error"}
        </Text>
      ) : (
        ""
      )}
      {loading ? (
        <ProgressRadial indeterminate style={{ marginLeft: "20px" }} />
      ) : (
        ""
      )}

      <Flex flexDirection="row">
        <Button
          onClick={handleCreate}
          disabled={!canCreate}
        >
          Create
        </Button>

        {subscriptionCreation.producerId && !canCreate && (
          <Text style={{ color: "red", marginLeft: "12px" }}>
            Subscriptions must be created with events.
          </Text>
        )}
      </Flex>
    </>
  );
};
